import { refs } from '../core/refs.js';
import { store } from '../core/store.js';

let enabled = false;
let clearTimer = null;
let activeMarkers = [];
let inspectorServices = {
  closeContextMenu: () => {},
  console: null,
  isMobile: () => false,
  postToPreview: () => {},
  setActiveMobileTab: () => {}
};

function postInspectMode() {
  inspectorServices.postToPreview({ type: 'front-my-end-inspect-mode', enabled });
}

function firstIndexOfAny(text, needles) {
  for (const needle of needles) {
    if (!needle) continue;
    const index = text.indexOf(needle);
    if (index !== -1) return { index, length: needle.length };
  }
  return null;
}

function scrollEditorToLine(editor, line) {
  const total = Math.max(1, editor.value.split('\n').length);
  editor.scrollTop = (line / total) * editor.scrollHeight - editor.clientHeight / 2;
}

function clearMarkers() {
  activeMarkers.forEach(({ marker, editor, onScroll }) => {
    editor.removeEventListener('scroll', onScroll);
    marker.remove();
  });
  activeMarkers = [];
}

function drawLineMarker(editor, line) {
  const wrapper = editor.closest('.editor-wrapper');
  if (!wrapper) return false;

  const marker = document.createElement('div');
  marker.className = 'inspect-line-marker';

  const update = () => {
    const style = window.getComputedStyle(editor);
    const lineHeight = parseFloat(style.lineHeight) || 21;
    const paddingTop = parseFloat(style.paddingTop) || 16;
    marker.style.top = `${editor.offsetTop + paddingTop + (line - 1) * lineHeight - editor.scrollTop}px`;
    marker.style.height = `${lineHeight}px`;
  };

  update();
  editor.addEventListener('scroll', update);
  wrapper.appendChild(marker);
  activeMarkers.push({ marker, editor, onScroll: update });
  return true;
}

function scrollEditorToIndex(editor, index) {
  const before = editor.value.slice(0, index);
  const line = before.split('\n').length;
  scrollEditorToLine(editor, line);
  return line;
}

function pulsePanel(panel) {
  if (!panel) return;
  panel.classList.add('inspect-pulse');
  clearTimeout(clearTimer);
  clearTimer = setTimeout(() => {
    document.querySelectorAll('.inspect-pulse').forEach((el) => el.classList.remove('inspect-pulse'));
  }, 2200);
}

function selectMatch(editor, panel, match) {
  if (!editor || !match) return false;
  const line = scrollEditorToIndex(editor, match.index);
  drawLineMarker(editor, line);
  pulsePanel(panel);
  return true;
}

function htmlNeedles(payload) {
  const needles = [];
  if (payload.id) {
    needles.push(`id="${payload.id}"`, `id='${payload.id}'`);
  }
  (payload.classes || []).forEach((className) => {
    needles.push(className);
  });
  if (payload.tagName) {
    needles.push(`<${payload.tagName.toLowerCase()}`);
  }
  return needles;
}

function cssNeedles(payload) {
  const needles = [];
  (payload.cssSelectors || []).forEach((selector) => needles.push(selector));
  if (payload.id) needles.push(`#${payload.id}`);
  (payload.classes || []).forEach((className) => needles.push(`.${className}`));
  if (payload.tagName) needles.push(payload.tagName.toLowerCase());
  return needles;
}

function handleInspectResult(payload) {
  const htmlMatch = firstIndexOfAny(refs.htmlEditor?.value || '', htmlNeedles(payload));
  const cssMatch = firstIndexOfAny(refs.cssEditor?.value || '', cssNeedles(payload));
  clearMarkers();

  if (inspectorServices.isMobile()) {
    inspectorServices.setActiveMobileTab('html');
  }

  const matchedHtml = selectMatch(refs.htmlEditor, refs.panelHtml, htmlMatch);
  const matchedCss = selectMatch(refs.cssEditor, refs.panelCss, cssMatch);

  const label = payload.id
    ? `#${payload.id}`
    : payload.classes?.length
      ? `.${payload.classes[0]}`
      : payload.tagName?.toLowerCase() || 'element';

  if (matchedHtml || matchedCss) {
    inspectorServices.console?.system(`Inspector matched ${label} in ${matchedHtml ? 'HTML' : ''}${matchedHtml && matchedCss ? ' and ' : ''}${matchedCss ? 'CSS' : ''}.`);
  } else {
    inspectorServices.console?.append('warn', `Inspector could not find a direct source match for ${label}.`);
  }
}

export function handlePreviewInspectResult(payload) {
  handleInspectResult(payload);
}

export function disableInspector() {
  if (enabled) {
    setEnabled(false);
  }
}

function setEnabled(next) {
  enabled = next;
  refs.inspectBtn?.setAttribute('aria-pressed', String(enabled));
  refs.inspectBtn?.classList.toggle('active', enabled);
  inspectorServices.closeContextMenu();
  if (!enabled) clearMarkers();
  postInspectMode();

  const message = enabled
    ? 'Inspector enabled. Click an element in the Preview panel to locate matching HTML and CSS. JS is not highlighted because runtime behavior can be generated dynamically by event handlers, timers, and state.'
    : 'Inspector disabled.';
  inspectorServices.console?.system(message);
}

export function registerPreviewInspector(services = {}) {
  inspectorServices = {
    ...inspectorServices,
    ...services
  };

  refs.inspectBtn?.addEventListener('click', (event) => {
    event.stopPropagation();
    setEnabled(!enabled);
  });

  refs.previewIframe?.addEventListener('load', () => {
    if (enabled) window.setTimeout(postInspectMode, 100);
  });

  window.addEventListener('front-my-end-preview-rendered', () => {
    if (enabled) window.setTimeout(postInspectMode, 100);
  });

  store.subscribe('activePreviewRoute', () => {
    if (enabled) window.setTimeout(postInspectMode, 100);
  });

  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && enabled) {
      setEnabled(false);
    }
  });
}
